import { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";

import { AuthProvider, useAuth } from "./context/AuthContext";
import AppLock from "./AppLock";
import Dashboard from "./screens/Dashboard";
import AbcSpecScreen from "./screens/AbcSpecScreen";
import MyTrackerScreen from "./screens/MyTrackerScreen";
import SetupWizard from "./screens/SetupWizard";
import LoginScreen from "./screens/LoginScreen";
import UserManagement from "./screens/UserManagement";
import AppFeedbackScreen from "./screens/AppFeedbackScreen";
import ErrorsScreen, { prefetchErrorsScreen, clearErrorsScreenCache, recentErrorCount } from "./screens/ErrorsScreen";
import PricingSettingsScreen from "./screens/PricingSettingsScreen";
import BankDetailsScreen from "./screens/BankDetailsScreen";
import FirebaseUsageScreen from "./screens/FirebaseUsageScreen";
import AppRegistryScreen from "./screens/AppRegistryScreen";
import BackupRestoreScreen from "./screens/BackupRestoreScreen";
import PasswordManagerScreen from "./screens/PasswordManagerScreen";
import DpdpSwitchScreen from "./screens/DpdpSwitchScreen";
import ProjectTrackerScreen from "./screens/ProjectTrackerScreen";
import CrmPipelineScreen from "./screens/crm/CrmPipelineScreen";
import CrmContactsScreen from "./screens/crm/CrmContactsScreen";
import CrmTasksScreen from "./screens/crm/CrmTasksScreen";
import CrmPnLScreen from "./screens/crm/CrmPnLScreen";
import adyantamLogo from "./assets/adyantam-logo.png";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase/config";
import { fetchLeads, fetchTasks, visibleTo } from "./crm/crmApi";
import { STAGES, todayStr } from "./crm/theme";

// Phone app: keep the menu button and page below the phone's status bar (time, battery).
if (Capacitor.isNativePlatform() && typeof document !== "undefined") {
  const vp = document.querySelector('meta[name="viewport"]');
  if (vp && !/viewport-fit/.test(vp.content)) vp.content += ", viewport-fit=cover";
}
const TOP_INSET = Capacitor.isNativePlatform()
  ? "max(env(safe-area-inset-top, 0px), var(--safe-area-inset-top, 0px), 30px)"
  : "env(safe-area-inset-top, 0px)";

// ─────────────────────────────────────────────────────────────────────────
// Menu look. Two one-tap choices, both remembered in this browser only:
//   1. Position: Side menu (floating card on the left) or Top menu (bar on top).
//   2. Colour of the open page: Purple (CRM) or Sunset (teal to orange).
//  - Side menu can also shrink to icons only ("Collapse menu").
//  - Phone (900px and below): always the small menu button + slide-in menu.
//  - Admin opens a small list (below the bar, or to the right of the card).
// Red numbers show what needs attention (Tasks: overdue, Pipeline: new leads,
// Project Tracker: new requests (Owner only), Admin > System Health: errors in
// the last 24 hours). They hide when the count is zero.
// Which pages each person can see is decided exactly as before (below).
// ─────────────────────────────────────────────────────────────────────────

const MOBILE_QUERY = "(max-width: 900px)";
const COLLAPSE_KEY = "crmNavCollapsed";
const LAYOUT_KEY = "crmNavLayout";
const THEME_KEY = "crmNavTheme";
const OWNER_EMAIL = "layakarudu@gmail.com";
const BADGE_REFRESH_MS = 5 * 60 * 1000; // numbers refresh every 5 minutes
const BADGE_MIN_GAP_MS = 20 * 1000;     // and when you change page (at most every 20 seconds)

const THEME_VARS = {
  purple: { "--abc-active-bg": "rgba(108,99,255,0.20)", "--abc-active-fg": "#b8afff" },
  sunset: { "--abc-active-bg": "linear-gradient(90deg,#1f8fa6,#e0883a)", "--abc-active-fg": "#ffffff" },
};

const NAV_CSS = `
  .abc-nav-item {
    display: flex; align-items: center; gap: 12px; width: 100%;
    padding: 9px 12px; border: none; border-radius: 9px;
    background: transparent; color: #e8eaf0; cursor: pointer;
    font-size: 14px; font-weight: 600; text-align: left;
    font-family: inherit; white-space: nowrap; box-sizing: border-box;
    transition: background .15s ease, color .15s ease;
  }
  .abc-nav-item:hover { background: rgba(255,255,255,0.07); }
  .abc-nav-item.active { background: var(--abc-active-bg, rgba(108,99,255,0.20)); color: var(--abc-active-fg, #b8afff); }
  .abc-nav-item:focus-visible { outline: 2px solid #6c63ff; outline-offset: -2px; }
  .abc-nav-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
  .abc-nav-slim .abc-nav-item { justify-content: center; padding: 10px 0; }
  .abc-nav-top .abc-nav-item { width: auto; padding: 8px 12px; gap: 8px; }
  .abc-nav-iconrow .abc-nav-item { justify-content: center; padding: 10px 0; }
  .abc-badge {
    min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; box-sizing: border-box;
    background: #ef4444; color: #fff; font-size: 11px; font-weight: 700; line-height: 1;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .abc-badge-corner { position: absolute; top: -7px; right: -9px; min-width: 16px; height: 16px; font-size: 10px; padding: 0 4px; box-shadow: 0 0 0 2px #1b1d27; }
  @keyframes abcSlideIn { from { transform: translateX(-105%); } to { transform: none; } }
`;

// Thin line icons, drawn here so nothing new needs to be installed.
const ICONS = {
  dashboard: (<><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></>),
  pipeline: <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />,
  contacts: (<><path d="M16 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z" /><circle cx="11" cy="10" r="2.5" /><path d="M7 17c.6-2 2-3 4-3s3.4 1 4 3" /></>),
  tasks: (<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>),
  pnl: (<><path d="M6 3h12" /><path d="M6 8h12" /><path d="M6 13l8.5 8" /><path d="M6 13h3" /><path d="M9 13c6.667 0 6.667-10 0-10" /></>),
  abc: (<><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" /></>),
  tracker: (<><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></>),
  folder: <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />,
  wand: (<><path d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M17.8 6.2L19 5M12.2 6.2L11 5" /><path d="M3 21l9-9" /></>),
  settings: (<><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></>),
  users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  alert: (<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></>),
  tag: (<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><path d="M7 7h.01" /></>),
  bank: (<><path d="M3 21h18" /><path d="M5 21v-9M9.5 21v-9M14.5 21v-9M19 21v-9" /><path d="M12 3l9 6H3z" /></>),
  barchart: (<><path d="M3 3v18h18" /><path d="M8 17v-6M13 17V7M18 17v-3" /></>),
  layers: (<><path d="M12 2l10 5-10 5L2 7z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></>),
  database: (<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" /></>),
  lock: (<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>),
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  "chevron-right": <path d="M9 18l6-6-6-6" />,
  "chevron-left": <path d="M15 18l-6-6 6-6" />,
  "chevron-down": <path d="M6 9l6 6 6-6" />,
  logout: (<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></>),
  menu: <path d="M3 6h18M3 12h18M3 18h18" />,
  "layout-top": (<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></>),
  "layout-side": (<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /></>),
  palette: (<><circle cx="13.5" cy="6.5" r=".5" /><circle cx="17.5" cy="10.5" r=".5" /><circle cx="8.5" cy="7.5" r=".5" /><circle cx="6.5" cy="12.5" r=".5" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" /></>),
};

function Icon({ name, size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      {ICONS[name] || null}
    </svg>
  );
}

// Remembered choices (this browser only). Never breaks the app if storage is blocked.
function readPref(key, allowed, fallback) {
  try {
    const v = localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch (e) {
    return fallback;
  }
}
function writePref(key, value) {
  try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
}

// True when the screen is phone / small-tablet width.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

// Window width, so the top bar can switch to icons only when it gets tight.
function useWindowWidth() {
  const [w, setW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1400));
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

// Esc closes an open list.
function useEscape(open, close) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Numbers for the menu. Reads the same data the pages already read, nothing new on the server.
function useMenuBadges(user, isOwner, module) {
  const [badges, setBadges] = useState({});
  const loadRef = useRef(null);
  const lastLoad = useRef(0);

  useEffect(() => {
    if (!user) { setBadges({}); loadRef.current = null; return undefined; }
    let alive = true;
    async function load() {
      lastLoad.current = Date.now();
      const next = {};
      try {
        const [tasks, leads] = await Promise.all([fetchTasks(), fetchLeads()]);
        const today = todayStr();
        // Tasks: still open and past their due date
        next.tasks = visibleTo(tasks, user).filter((t) => t.status !== "done" && t.dueDate && t.dueDate < today).length;
        // Pipeline: new leads nobody has called yet
        next.pipeline = visibleTo(leads, user).filter((l) => !l.stage || l.stage === "New Lead" || !STAGES.includes(l.stage)).length;
      } catch (e) { /* the menu simply shows no numbers */ }
      if (isOwner) {
        try {
          // Project Tracker (Owner only): requests from other people still waiting
          const snap = await getDocs(query(collection(db, "projectTracker"), where("status", "==", "Pending")));
          next["project-tracker"] = snap.docs.filter((d) => String(d.data().createdBy || "").trim().toLowerCase() !== OWNER_EMAIL).length;
        } catch (e) { /* ignore */ }
      }
      if (alive) setBadges((old) => ({ ...old, ...next }));
    }
    loadRef.current = load;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, BADGE_REFRESH_MS);
    return () => { alive = false; clearInterval(timer); };
  }, [user?.uid, user?.email, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also refresh when you change page, but not more often than every 20 seconds.
  useEffect(() => {
    if (loadRef.current && Date.now() - lastLoad.current > BADGE_MIN_GAP_MS) loadRef.current();
  }, [module]);

  return badges;
}

function NavButton({ icon, label, active, onClick, slim, right, badge }) {
  const n = Number(badge) || 0;
  const text = n > 99 ? "99+" : String(n);
  return (
    <button
      type="button"
      className={"abc-nav-item" + (active ? " active" : "")}
      onClick={onClick}
      title={slim ? label + (n > 0 ? " (" + text + ")" : "") : undefined}
      aria-label={n > 0 ? label + ", " + text + " need attention" : undefined}
      aria-current={active ? "page" : undefined}
    >
      <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
        <Icon name={icon} />
        {slim && n > 0 && <span className="abc-badge abc-badge-corner">{text}</span>}
      </span>
      {!slim && <span className="abc-nav-label">{label}</span>}
      {!slim && n > 0 && <span className="abc-badge">{text}</span>}
      {!slim && right}
    </button>
  );
}

const CARD_BG = "#1b1d27";
const CARD_BORDER = "1px solid #2e3250";

// The Admin list: a small card that floats beside or below the Admin button.
function AdminFlyout({ pos, items, current, onPick, onClose, badges }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60 }} />
      <div
        role="menu"
        style={{ position: "fixed", left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, overflowY: "auto", width: 240, zIndex: 61, boxSizing: "border-box", padding: 8, background: CARD_BG, border: CARD_BORDER, borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", gap: 2 }}
      >
        {items.map(([id, label, icon]) => (
          <NavButton key={id} icon={icon} label={label} active={current === id} onClick={() => onPick(id)} badge={badges[id]} />
        ))}
      </div>
    </>
  );
}

function LogoBlock({ showName }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <img src={adyantamLogo} alt="Adyantam" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      {showName && <div style={{ fontSize: 13, fontWeight: 700, color: "#8b7cff", lineHeight: 1.2 }}>Adyantam Building Circle®</div>}
    </div>
  );
}

// ── Side menu (floating card on the left; slide-in drawer on a phone) ──
function SideMenu({ tabs, settingsItems, current, onGo, userName, onLogout, collapsed, onToggleCollapse, onToggleLayout, theme, onToggleTheme, mobile, onClose, badges }) {
  const [adminOpen, setAdminOpen] = useState(false);
  const [flyPos, setFlyPos] = useState({ left: 0, bottom: 10, maxHeight: 400 });
  const slim = collapsed && !mobile;
  const adminActive = settingsItems.some(([id]) => id === current);
  useEscape(adminOpen, () => setAdminOpen(false));

  function pick(id) {
    setAdminOpen(false);
    onGo(id);
  }

  function toggleAdmin(e) {
    if (mobile) { setAdminOpen((v) => !v); return; }
    if (adminOpen) { setAdminOpen(false); return; }
    const btn = e.currentTarget.getBoundingClientRect();
    const navEl = e.currentTarget.closest("nav");
    const right = navEl ? navEl.getBoundingClientRect().right : btn.right;
    setFlyPos({
      left: right + 8,
      bottom: Math.max(10, window.innerHeight - btn.bottom),
      maxHeight: Math.max(160, btn.bottom - 10),
    });
    setAdminOpen(true);
  }

  const navStyle = mobile
    ? { position: "fixed", top: 0, left: 0, bottom: 0, width: 270, zIndex: 51, borderRadius: "0 14px 14px 0", animation: "abcSlideIn .18s ease-out" }
    : { margin: 10, borderRadius: 14, width: slim ? 68 : 236, flexShrink: 0, transition: "width .15s ease" };

  return (
    <>
      {mobile && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 50 }} />}
      <nav
        className={slim ? "abc-nav-slim" : ""}
        style={{ ...navStyle, display: "flex", flexDirection: "column", boxSizing: "border-box", padding: 8, paddingTop: mobile ? `calc(8px + ${TOP_INSET})` : 8, background: CARD_BG, border: CARD_BORDER, color: "#fff", fontFamily: "inherit" }}
      >
        {/* Logo and name */}
        <div style={{ padding: slim ? "6px 0 12px" : "6px 8px 12px", display: "flex", justifyContent: slim ? "center" : "flex-start" }}>
          <LogoBlock showName={!slim} />
        </div>

        {/* Pages (scrolls only if the window is very short) */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
          {tabs.map(([id, label, icon]) => (
            <NavButton key={id} icon={icon} label={label} active={current === id} onClick={() => pick(id)} slim={slim} badge={badges[id]} />
          ))}
        </div>

        {/* Admin stays pinned here, so it is always visible */}
        {settingsItems.length > 0 && (
          <div style={{ flexShrink: 0, borderTop: "1px solid #2e3250", marginTop: 6, paddingTop: 6 }}>
            <NavButton
              icon="settings"
              label="Admin"
              active={adminActive || adminOpen}
              onClick={toggleAdmin}
              slim={slim}
              badge={badges.admin}
              right={<Icon name={mobile && adminOpen ? "chevron-down" : "chevron-right"} size={16} />}
            />
            {/* Phone: Admin list opens inside the menu */}
            {mobile && adminOpen && (
              <div style={{ marginLeft: 14, paddingLeft: 6, borderLeft: "1px solid #2e3250", display: "flex", flexDirection: "column", gap: 2, maxHeight: "38vh", overflowY: "auto" }}>
                {settingsItems.map(([id, label, icon]) => (
                  <NavButton key={id} icon={icon} label={label} active={current === id} onClick={() => pick(id)} badge={badges[id]} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Your name, the three small buttons, sign out */}
        <div style={{ flexShrink: 0, borderTop: "1px solid #2e3250", marginTop: 6, paddingTop: 6 }}>
          {!slim && (
            <div title={userName} style={{ fontSize: 12, color: "#aab2c5", padding: "2px 12px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {userName}
            </div>
          )}
          {mobile ? (
            <NavButton icon="palette" label={"Colour: " + (theme === "sunset" ? "Sunset" : "Purple")} onClick={onToggleTheme} />
          ) : (
            <div className="abc-nav-iconrow" style={{ display: "flex", flexDirection: slim ? "column" : "row", gap: 2 }}>
              <div style={{ flex: 1 }}><NavButton icon="layout-top" label="Top menu" onClick={onToggleLayout} slim /></div>
              <div style={{ flex: 1 }}><NavButton icon="palette" label={"Colour: " + (theme === "sunset" ? "Sunset" : "Purple")} onClick={onToggleTheme} slim /></div>
              <div style={{ flex: 1 }}><NavButton icon={collapsed ? "chevron-right" : "chevron-left"} label={collapsed ? "Expand menu" : "Collapse menu"} onClick={onToggleCollapse} slim /></div>
            </div>
          )}
          <NavButton icon="logout" label="Sign Out" onClick={onLogout} slim={slim} />
        </div>
      </nav>

      {/* Desktop: Admin list opens to the right of the card */}
      {adminOpen && !mobile && (
        <AdminFlyout pos={flyPos} items={settingsItems} current={current} onPick={pick} onClose={() => setAdminOpen(false)} badges={badges} />
      )}
    </>
  );
}

// ── Top menu (floating bar across the top; desktop only) ──
function TopMenu({ tabs, settingsItems, current, onGo, userName, onLogout, onToggleLayout, theme, onToggleTheme, width, badges }) {
  const [adminOpen, setAdminOpen] = useState(false);
  const [flyPos, setFlyPos] = useState({ left: 10, top: 60, maxHeight: 400 });
  useEscape(adminOpen, () => setAdminOpen(false));
  const hasAdmin = settingsItems.length > 0;
  const adminActive = settingsItems.some(([id]) => id === current);
  // When the bar gets tight, only the open page keeps its name; the rest show icons.
  const iconsOnly = width < 400 + (tabs.length + (hasAdmin ? 1 : 0)) * 120;

  function pick(id) {
    setAdminOpen(false);
    onGo(id);
  }

  function toggleAdmin(e) {
    if (adminOpen) { setAdminOpen(false); return; }
    const btn = e.currentTarget.getBoundingClientRect();
    setFlyPos({
      left: Math.max(10, Math.min(btn.left, window.innerWidth - 250)),
      top: btn.bottom + 8,
      maxHeight: Math.max(160, window.innerHeight - btn.bottom - 18),
    });
    setAdminOpen(true);
  }

  return (
    <>
      <nav
        className="abc-nav-top"
        style={{ margin: "10px 10px 0", padding: "6px 8px", borderRadius: 14, flexShrink: 0, display: "flex", alignItems: "center", gap: 8, boxSizing: "border-box", background: CARD_BG, border: CARD_BORDER, color: "#fff", fontFamily: "inherit" }}
      >
        <div style={{ padding: "0 6px 0 4px", flexShrink: 0 }}>
          <LogoBlock showName={!iconsOnly} />
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 2, overflowX: "auto" }}>
          {tabs.map(([id, label, icon]) => (
            <NavButton key={id} icon={icon} label={label} active={current === id} onClick={() => pick(id)} slim={iconsOnly && current !== id} badge={badges[id]} />
          ))}
          {hasAdmin && (
            <>
              <div style={{ width: 1, alignSelf: "stretch", background: "#2e3250", margin: "4px 4px", flexShrink: 0 }} />
              <NavButton
                icon="settings"
                label="Admin"
                active={adminActive || adminOpen}
                onClick={toggleAdmin}
                slim={iconsOnly && !(adminActive || adminOpen)}
                badge={badges.admin}
                right={<Icon name="chevron-down" size={16} />}
              />
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          {width >= 1300 && (
            <span title={userName} style={{ fontSize: 12, color: "#aab2c5", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 6px" }}>{userName}</span>
          )}
          <NavButton icon="layout-side" label="Side menu" onClick={onToggleLayout} slim />
          <NavButton icon="palette" label={"Colour: " + (theme === "sunset" ? "Sunset" : "Purple")} onClick={onToggleTheme} slim />
          <NavButton icon="logout" label="Sign Out" onClick={onLogout} slim={iconsOnly} />
        </div>
      </nav>

      {adminOpen && (
        <AdminFlyout pos={flyPos} items={settingsItems} current={current} onPick={pick} onClose={() => setAdminOpen(false)} badges={badges} />
      )}
    </>
  );
}

function AppShell() {
  const { user, loading, logout } = useAuth();
  const isSales = user?.role === "Sales";
  const isSalesHead = user?.role === "SalesHead";
  const isSalesTeam = isSales || isSalesHead;
  // DPDP switch is visible only to the Owner (also checked on the server).
  const isDpdpOwner = (user?.email || "").trim().toLowerCase() === "layakarudu@gmail.com";
  const [module, setModule] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const width = useWindowWidth();
  const [errCount, setErrCount] = useState(0); // System Health errors in the last 24 hours
  const menuBadges = useMenuBadges(user, isDpdpOwner, module);

  // Remembered one-tap choices (this browser only)
  const [collapsed, setCollapsed] = useState(() => readPref(COLLAPSE_KEY, ["0", "1"], "0") === "1");
  const [layout, setLayout] = useState(() => readPref(LAYOUT_KEY, ["side", "top"], "side"));
  const [theme, setTheme] = useState(() => readPref(THEME_KEY, ["purple", "sunset"], "purple"));
  function toggleCollapse() {
    const next = !collapsed;
    setCollapsed(next);
    writePref(COLLAPSE_KEY, next ? "1" : "0");
  }
  function toggleLayout() {
    const next = layout === "side" ? "top" : "side";
    setLayout(next);
    writePref(LAYOUT_KEY, next);
  }
  function toggleTheme() {
    const next = theme === "purple" ? "sunset" : "purple";
    setTheme(next);
    writePref(THEME_KEY, next);
  }

  // System Health reads logs from Google, which takes a few seconds. Fetch them quietly right after sign-in
  // (only for people who can open that screen) so it opens instantly; forget them on sign-out.
  useEffect(() => {
    clearErrorsScreenCache();
    setErrCount(0);
    if (user && !isSalesTeam) {
      const p = prefetchErrorsScreen({ jobs: isDpdpOwner });
      if (p && p.then) p.then(() => setErrCount(recentErrorCount()));
    }
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  // Recount from the remembered result (no new fetching) whenever you change page.
  useEffect(() => { setErrCount(recentErrorCount()); }, [module]);
  // Never carry the open screen over to another user (or after sign-out): go back to the Dashboard.
  const [lastUserEmail, setLastUserEmail] = useState(user?.email || "");
  if (lastUserEmail !== (user?.email || "")) {
    setLastUserEmail(user?.email || "");
    setModule("dashboard");
    setMobileMenuOpen(false);
  }

  const crmTabs = [
    ["pipeline", "Pipeline", "pipeline"],
    ["contacts", "Contacts", "contacts"],
    ["tasks", "Tasks", "tasks"],
    ["pnl", "P&L", "pnl"],
  ];

  const tabs = isSalesTeam
    ? [
        ["dashboard", "Dashboard", "dashboard"],
        ...crmTabs,
        ["project-tracker", "Project Tracker", "folder"],
        ["setup", "Setup Wizard", "wand"],
      ]
    : [
        ["dashboard", "Dashboard", "dashboard"],
        ...crmTabs,
        ["project-tracker", "Project Tracker", "folder"],
        ["setup", "Setup Wizard", "wand"],
      ];
  const settingsItems = isSalesHead
    ? [
        ["user-management", "User Management", "users"],
        ["pricing-settings", "Pricing Settings", "tag"],
        ["bank-details", "Bank Details", "bank"],
      ]
    : isSales
    ? []
    : [
        ["user-management", "User Management", "users"],
        ["app-feedback", "App Feedback", "message"],
        ["errors", "System Health", "alert"],
        ["pricing-settings", "Pricing Settings", "tag"],
        ["bank-details", "Bank Details", "bank"],
        ["firebase-usage", "Firebase Usage", "barchart"],
        ["app-registry", "App Registry", "layers"],
        ...(isDpdpOwner ? [["backup-restore", "Backup & Restore", "database"]] : []),
        ["password-manager", "Password Manager", "lock"],
        ...(isDpdpOwner ? [["dpdp-switch", "DPDP Switch", "shield"]] : []),
      ];

  const effectiveModule = module;

  function goTo(id) {
    setModule(id);
    setMobileMenuOpen(false);
  }

  const render = () => {
    switch (effectiveModule) {
      case "abc":
        return <AbcSpecScreen user={user} />;
      case "tracker":
        return <MyTrackerScreen user={user} />;
      case "setup":
        return <SetupWizard user={user} onComplete={() => { window.open("https://abc.adyantam.in/", "_blank"); }} />;
      case "project-tracker":
        return <ProjectTrackerScreen user={user} />;
      case "user-management":
        return <UserManagement />;
      case "app-feedback":
        return <AppFeedbackScreen />;
      case "errors":
        return <ErrorsScreen />;
      case "pricing-settings":
        return <PricingSettingsScreen />;
      case "bank-details":
        return <BankDetailsScreen />;
      case "firebase-usage":
        return <FirebaseUsageScreen />;
      case "app-registry":
        return <AppRegistryScreen />;
      case "backup-restore":
        return isDpdpOwner ? <BackupRestoreScreen /> : null;
      case "password-manager":
        return <PasswordManagerScreen />;
      case "dpdp-switch":
        return isDpdpOwner ? <DpdpSwitchScreen /> : <Dashboard user={user} />;
      case "pipeline":
        return <CrmPipelineScreen user={user} />;
      case "contacts":
        return <CrmContactsScreen user={user} />;
      case "tasks":
        return <CrmTasksScreen user={user} />;
      case "pnl":
        return <CrmPnLScreen user={user} />;
      default:
        return <Dashboard user={user} />;
    }
  };

  if (loading) {
    return <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#111217", color: "#fff" }}>Loading platform access…</div>;
  }

  if (!user) {
    return <LoginScreen />;
  }

  const userName = user.name || user.email;
  const topLayout = !isMobile && layout === "top";
  const badges = { ...menuBadges, errors: errCount, admin: errCount };
  const anyBadge = (menuBadges.tasks || 0) + (menuBadges.pipeline || 0) + (menuBadges["project-tracker"] || 0) + errCount > 0;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: topLayout ? "column" : "row", background: "#111217", ...THEME_VARS[theme] }}>
      <style>{NAV_CSS}</style>

      {/* Desktop, Side menu: floating card on the left */}
      {!isMobile && !topLayout && (
        <SideMenu
          tabs={tabs}
          settingsItems={settingsItems}
          current={effectiveModule}
          onGo={goTo}
          userName={userName}
          onLogout={logout}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          onToggleLayout={toggleLayout}
          theme={theme}
          onToggleTheme={toggleTheme}
          mobile={false}
          badges={badges}
        />
      )}

      {/* Desktop, Top menu: floating bar across the top */}
      {topLayout && (
        <TopMenu
          tabs={tabs}
          settingsItems={settingsItems}
          current={effectiveModule}
          onGo={goTo}
          userName={userName}
          onLogout={logout}
          onToggleLayout={toggleLayout}
          theme={theme}
          onToggleTheme={toggleTheme}
          width={width}
          badges={badges}
        />
      )}

      {/* Phone: small menu button, menu slides in from the left */}
      {isMobile && (
        <>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Menu"
            style={{ position: "fixed", top: `calc(8px + ${TOP_INSET})`, left: 8, zIndex: 40, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: CARD_BORDER, borderRadius: 10, background: CARD_BG, color: "#e8eaf0", cursor: "pointer" }}
          >
            <Icon name="menu" />
            {anyBadge && <span style={{ position: "absolute", top: 5, right: 5, width: 10, height: 10, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 0 2px " + CARD_BG }} />}
          </button>
          {mobileMenuOpen && (
            <SideMenu
              tabs={tabs}
              settingsItems={settingsItems}
              current={effectiveModule}
              onGo={goTo}
              userName={userName}
              onLogout={logout}
              collapsed={false}
              onToggleCollapse={toggleCollapse}
              onToggleLayout={toggleLayout}
              theme={theme}
              onToggleTheme={toggleTheme}
              mobile={true}
              onClose={() => setMobileMenuOpen(false)}
              badges={badges}
            />
          )}
        </>
      )}

      {/* Page content */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", paddingTop: isMobile ? `calc(56px + ${TOP_INSET})` : 0 }}>{render()}</div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppLock>
        <AppShell />
      </AppLock>
    </AuthProvider>
  );
}
